import {observer} from 'mobx-react';
import '../geography-filter.scss';
import {ReactNode, useContext, useEffect, useMemo} from 'react';
import {NetworkContext} from '../../../lib/context';
import {FilterProps} from '../filter-types';
import FilterContainer from '../filter-container';
import {useSpecialtyDataSource} from '../../hooks';
import {
    useDistanceCalcTypeFilter,
    useAdequacyStandardFilter,
    useCountyFilter,
    useSpecialtyDropdownFilter
} from '../hooks';

type Props = {
    showRosterSelection?: boolean;
    hideSpecialtyFilter?: boolean;
    activeTab?: number;
};

const NetworkAnalysisGeographyFilter = observer((props: Props) => {
    const {showRosterSelection = false, hideSpecialtyFilter = false, activeTab} = props;
    const noStore = useContext(NetworkContext);

    useEffect(() => {
        if (noStore.runId &&
            noStore.availableGeographyFilters.length === 0 &&
            !noStore.loadingGeographyData) {
            noStore.loadGeographyData().catch((err) => {
                noStore.handleApiError(err, 'Failed to Load Geography Filters', () => noStore.loadGeographyData());
            });
        }
    }, [noStore.runId]);

    const distanceCalcTypeFilter = useDistanceCalcTypeFilter(
        noStore.distanceCalcType,
        (e: string) => { noStore.distanceCalcType = e; }
    );

    const adequacyStandardFilter = useAdequacyStandardFilter(
        noStore,
        noStore.loadingGeographyData,
        (e: string) => { noStore.setGeographyFilter('standard_type', e); }
    );

    const standardVersionFilter: FilterProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedGeographyFilter.standard_type,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'standard_version',
            geoFilterArgs,
            '',
            'DESC',
        );
        const onChange = (e: string) => {
            noStore.setGeographyFilter('standard_version', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item,
        ];
        return {
            label: 'Standard Version',
            data,
            value: noStore.appliedGeographyFilter.standard_version,
            required: true,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'standard-version-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        distanceCalcTypeFilter,
        adequacyStandardFilter,
        noStore.appliedGeographyFilter.standard_version
    ]);

    const patientRosterFilter: FilterProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedGeographyFilter.standard_type,
            standard_version: noStore.appliedGeographyFilter.standard_version,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'patient_roster_name',
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            noStore.setGeographyFilter('patient_roster_name', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item,
        ];
        return {
            label: 'Patient Roster',
            data,
            value: noStore.appliedGeographyFilter.patient_roster_name,
            required: true,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'patient-roster-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        adequacyStandardFilter,
        standardVersionFilter,
        noStore.appliedGeographyFilter.patient_roster_name
    ]);

    const stateFilter: FilterProps = useMemo(() => {
        const geoFilterArgs = {
            patient_roster_name: noStore.appliedGeographyFilter.patient_roster_name,
            standard_type: noStore.appliedGeographyFilter.standard_type,
            standard_version: noStore.appliedGeographyFilter.standard_version,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'state_additional_info.state',
            geoFilterArgs
        );
        const onChange = (e: string) => {
            noStore.setGeographyFilter('state', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        return {
            label: 'State',
            data,
            value: noStore.appliedGeographyFilter.state,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'state-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        noStore.appliedGeographyFilter.state,
        distanceCalcTypeFilter,
        adequacyStandardFilter,
        standardVersionFilter,
    ]);

    const countyFilter = useCountyFilter(
        noStore,
        noStore.loadingGeographyData,
        (e: string) => { noStore.setGeographyFilter('county', e); },
        showRosterSelection,
        activeTab
    );

    // Shared data source selection - same as specialty filter table
    const specialtyDataSource = useSpecialtyDataSource(
        noStore.defaultComplianceScores,
        noStore.complianceScores,
    );

    const specialtyFilter = useSpecialtyDropdownFilter(
        noStore,
        specialtyDataSource,
        (e: string) => { noStore.setSpecialtyFilter(e); },
        showRosterSelection,
        activeTab
    );

    useEffect(() => {
        if (!noStore.appliedGeographyFilter.standard_type) {
            noStore.setSpecialtyFilter('');
        }
    }, [noStore.appliedGeographyFilter.standard_type]);

    const filterConfiguration: Array<FilterProps> = [
        distanceCalcTypeFilter,
        adequacyStandardFilter,
        standardVersionFilter,
        patientRosterFilter,
        stateFilter,
        countyFilter,
        ...(hideSpecialtyFilter ? [] : [specialtyFilter]),
    ];

    return (
        <FilterContainer
            filterConfiguration={filterConfiguration}
        />
    );
});

export default NetworkAnalysisGeographyFilter;
